/* Present-State Coherence Assessment (160 max) */

const COHERENCE = {
  title: 'Present‑State Coherence Assessment',
  subtitle: 'Baseline over the last 90 days · Coherence before velocity. Truth before strategy.',
  realms: {
    physical: {
      max: 35,
      path: 'Dysregulation → Reliable vitality → Quiet authority',
      statements: [
        'I maintain consistent sleep/wake times and honor my body\'s need for recovery without override. (1=chronically override signals, 5=reliable rhythms established)',
        'I respond to physical signals (pain, fatigue, hunger) before they become emergencies. (1=ignore until crisis, 5=preventive responsiveness)',
        'My energy management relies on sustainable practices rather than stimulants, grit, or adrenaline. (1=performance physiology dominant, 5=natural vitality baseline)',
        'I have non-negotiable schedule blocks for physical maintenance (sleep, movement, recovery). (1=no boundaries/reactive, 5=protected and honored)',
        'My physical health decisions reflect internal authority rather than outsourcing to coaches/programs. (1=somatic outsourcing, 5=embodied governance)',
        'My libido and sexual vitality are present and reliable, not collapsed or forced. (1=significant collapse/dysfunction, 5=healthy and coherent)',
        'I maintain physical practices that are sustainable over years, not just performative sprints. (1=boom/bust cycles, 5=steady state reliability)'
      ]
    },
    mental: {
      max: 30,
      path: 'Defending mind → Clear perception → Transparent presence',
      statements: [
        'I can acknowledge difficult realities without defaulting to “I\'m fine” or “it\'s just a busy season.” (1=narrative control, 5=reality-tested narratives)',
        'I notice when I\'m intellectualizing emotions or strategizing intimacy to avoid feeling. (1=cognitive bypass, 5=thinking serves feeling)',
        'I hold my beliefs and identity lightly, able to update them when reality contradicts them. (1=rigid belief capture, 5=humble/adaptive)',
        'My leadership thinking includes nuance beyond strong/weak or winning/losing binaries. (1=binary collapse, 5=complexity tolerance)',
        'I can distinguish between what I genuinely believe vs. what I inherited or perform for approval. (1=external authority, 5=internal discernment)',
        'My mental narratives support coherence rather than image protection or spin. (1=reality bent to identity, 5=signal over story)'
      ]
    },
    emotional: {
      max: 30,
      path: 'Numbness/volatility → Honest affect → Charge moves without sticking',
      statements: [
        'I can name my emotional state (fear, grief, joy, shame) without defensiveness or performance. (1=numbness/avoidance, 5=honest naming)',
        'I experience and express emotions beyond anger (sadness, fear, tenderness, joy). (1=anger only, 5=full range accessible)',
        'I stay present with emotional charge without collapsing, numbing, or projecting onto others. (1=volatility/shutdown, 5=regulated containment)',
        'I take responsibility for metabolizing my own stress rather than outsourcing it to my partner/family. (1=emotional outsourcing, 5=emotional sovereignty)',
        'I can show uncertainty, need, or softness without shame or invulnerability performance. (1=shame management, 5=authentic without armor)',
        'My calm comes from actual groundedness, not disconnection or avoidance. (1=avoidant competence, 5=connected presence)'
      ]
    },
    spiritual: {
      max: 30,
      path: 'Emptiness/bypass → Grounded devotion → Unwavering presence',
      statements: [
        'My work and success are connected to genuine meaning and devotion, not just achievement. (1=meaning starvation, 5=grounded purpose)',
        'I have a devotional practice or orienting framework that isn\'t performative or bypassing. (1=bypass/cynicism, 5=integrated devotion)',
        'I can be still without compulsive stimulation or productivity to avoid emptiness. (1=energetic leakage, 5=stillness capacity)',
        'My sense of purpose serves coherence rather than extracting from self or others. (1=mission as mask, 5=aligned contribution)',
        'I have clarity about what I\'m devoted to beyond professional identity or external validation. (1=drift/confusion, 5=clear orientation)',
        'My energy and attention are intentionally allocated rather than scattered or reactive. (1=attention chaos, 5=energetic sovereignty)'
      ]
    },
    relational: {
      max: 35,
      path: 'Pursue/withdraw → Consent clarity → Devotional union',
      statements: [
        'I have explicit agreements and roles in my key relationships rather than assumed expectations. (1=consent blur/power default, 5=explicit clarity)',
        'I receive feedback from intimate relationships without interpreting it as disrespect or attack. (1=feedback intolerance, 5=openness to input)',
        'I execute clean repairs after ruptures rather than substituting distance, gifts, or explanations. (1=repair avoidance, 5=repair protocol reliable)',
        'I lead differently at home than at work, adjusting power dynamics to context. (1=power defaulting, 5=context-appropriate leadership)',
        'I earn trust through reliability rather than demanding loyalty or assuming attachment. (1=attachment outsourcing, 5=trust through proof)',
        'I ask for consent regarding time, emotional labor, and physical intimacy rather than assuming access. (1=entitlement patterns, 5=consent clarity)',
        'My relationships reflect mutual trust and safety rather than fear, appeasement, or chronic rupture. (1=relational debt, 5=relational coherence)'
      ]
    }
  }
};

function buildRows(tbodyId, realmKey, statements){
  const tbody = document.getElementById(tbodyId);
  tbody.innerHTML = '';
  statements.forEach((stmt, idx) => {
    const q = idx + 1;
    const name = `${realmKey}_${q}`;
    const tr = document.createElement('tr');
    const tdStmt = document.createElement('td');
    tdStmt.className = '';
    tdStmt.textContent = stmt;
    tr.appendChild(tdStmt);

    for(let v=1; v<=5; v++){
      const td = document.createElement('td');
      const label = document.createElement('label');
      label.className = 'rate';
      const input = document.createElement('input');
      input.type = 'radio';
      input.name = name;
      input.value = String(v);
      input.required = true;
      const span = document.createElement('span');
      span.textContent = String(v);
      label.appendChild(input);
      label.appendChild(span);
      td.appendChild(label);
      tr.appendChild(td);
    }

    tbody.appendChild(tr);
  });
}

function realmTotal(realmKey, count){
  let total = 0;
  for(let i=1;i<=count;i++){
    const el = document.querySelector(`input[name="${realmKey}_${i}"]:checked`);
    if(el) total += Number(el.value);
  }
  return total;
}

function update(){
  const scores = {};
  for(const [k, realm] of Object.entries(COHERENCE.realms)){
    scores[k] = realmTotal(k, realm.statements.length);
    document.getElementById(`${k}Total`).textContent = String(scores[k]);
    document.getElementById(`ws${k[0].toUpperCase()+k.slice(1)}`).textContent = String(scores[k]);
  }

  const total = Object.values(scores).reduce((a,b)=>a+b,0);
  const percent = Math.round((total/160)*100);
  document.getElementById('totalScore').textContent = String(total);
  document.getElementById('totalPercent').textContent = String(percent);
  document.getElementById('wsTotal').textContent = String(total);

  // Pattern lock (only if all answered)
  // IMPORTANT: querySelectorAll('[required]') does NOT work reliably on dynamic radio-groups.
  // We compute completion by checking that each question-group has a checked value.
  const lock = document.getElementById('patternLock');

  const realmKeys = Object.keys(COHERENCE.realms);
  let allAnswered = true;

  for (const rk of realmKeys) {
    const qCount = COHERENCE.realms[rk].statements.length;
    for (let i = 1; i <= qCount; i++) {
      const el = document.querySelector(`input[name="${rk}_${i}"]:checked`);
      if (!el) {
        allAnswered = false;
        break;
      }
    }
    if (!allAnswered) break;
  }

  if (!allAnswered) {
    lock.textContent = 'Answer all statements to reveal your lowest realm.';
    return { scores, total, percent, ready: false };
  }

  let lowestKey = null;
  let lowestPct = Infinity;
  for(const [k, realm] of Object.entries(COHERENCE.realms)){
    const pct = scores[k]/realm.max;
    if(pct < lowestPct){ lowestPct = pct; lowestKey = k; }
  }

  const names = {
    physical:'Physical',
    mental:'Mental',
    emotional:'Emotional',
    spiritual:'Spiritual/Energetic',
    relational:'Relational'
  };

  lock.innerHTML = `<b>Lowest realm:</b> ${names[lowestKey]} (${scores[lowestKey]}/${COHERENCE.realms[lowestKey].max}) — this is your primary developmental territory right now.`;

  return { scores, total, percent, ready:true, lowestKey };
}

function stageFromTotal(total){
  if(total >= 128) return {label:'HIGH COHERENCE', readiness:'Forté, Fire, Live Fortified'};
  if(total >= 96) return {label:'BUILDING COHERENCE', readiness:'Fort, Forge'};
  if(total >= 64) return {label:'EARLY AWARENESS', readiness:'Ignite'};
  return {label:'FOUNDATIONAL NEED', readiness:'Pre‑Ignite'};
}

function buildReport(state){
  const realmOrder = ['physical','mental','emotional','spiritual','relational'];
  const realmNames = {
    physical:'Physical',
    mental:'Mental',
    emotional:'Emotional',
    spiritual:'Spiritual/Energetic',
    relational:'Relational'
  };

  const stage = stageFromTotal(state.total);

  const summaryHtml = `
    <h2 class="h2">Executive Summary</h2>
    <div class="kv">
      <div class="totalBox"><div class="small">Total score</div><b>${state.total}</b> / 160</div>
      <div class="totalBox"><div class="small">Total percent</div><b>${state.percent}%</b></div>
    </div>
    <div class="hr"></div>
    <div class="totalBox"><div class="small">Stage placement</div><b>${stage.label}</b><div class="small">Readiness: ${stage.readiness}</div></div>
    <div class="hr"></div>
    <div class="totalBox"><div class="small">Pattern lock</div><b>${realmNames[state.lowestKey]} (${state.scores[state.lowestKey]}/${COHERENCE.realms[state.lowestKey].max})</b> — lowest realm</div>
  `;

  const realmRowsHtml = realmOrder.map(k => {
    const max = COHERENCE.realms[k].max;
    const score = state.scores[k];
    const pct = Math.round((score/max)*100);
    const path = COHERENCE.realms[k].path;
    return `<tr>
      <td><b>${realmNames[k]}</b></td>
      <td>${score}/${max}</td>
      <td>${pct}%</td>
      <td>${fortifyEscapeHtml(path)}</td>
    </tr>`;
  }).join('');

  const extraHtml = `
    <div class="hr"></div>
    <h2 class="h2">What to do next (C3 Engine)</h2>
    <div class="quote">
      <b>CLEAR</b>: Name the pattern without self‑attack. Remove false containers.<br>
      <b>CONNECT</b>: Restore contact with body signal and relational truth.<br>
      <b>CREATE</b>: Build governance through proof.
    </div>

    <h2 class="h2" style="margin-top:18px;">Applied Sovereignty proof vectors</h2>
    <ul class="small">
      <li>Agreements · Boundaries · Schedules · Roles</li>
      <li>Repair Protocols (within 72 hours) · Governance Structures</li>
      <li>Sustained Silence · Non‑Participation</li>
    </ul>
  `;

  openBrandedReportWindow({
    title: COHERENCE.title,
    subtitle: COHERENCE.subtitle,
    summaryHtml,
    realmRowsHtml,
    extraHtml,
    footerText: 'DeanHobson.com | The Sovereignty Path'
  });
}

document.addEventListener('DOMContentLoaded', () => {
  // build question tables
  buildRows('physicalRows','physical',COHERENCE.realms.physical.statements);
  buildRows('mentalRows','mental',COHERENCE.realms.mental.statements);
  buildRows('emotionalRows','emotional',COHERENCE.realms.emotional.statements);
  buildRows('spiritualRows','spiritual',COHERENCE.realms.spiritual.statements);
  buildRows('relationalRows','relational',COHERENCE.realms.relational.statements);

  const form = document.getElementById('coherenceForm');
  form.addEventListener('change', update);
  update();

  let hasCalculatedResults = false;

  document.getElementById('btnViewReport').addEventListener('click', () => {
    const state = update();
    hasCalculatedResults = !!state.ready;
    if(!hasCalculatedResults){
      alert('Complete all statements to view the full report.');
      return;
    }
    buildReport(state);
  });
});